import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Text,
  Badge,
  Flex,
  useToast,
  useColorModeValue,
  Spinner,
  Button,
  Icon,
  Image,
  HStack,
  Spacer,
  useDisclosure,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Grid,
} from "@chakra-ui/react";
import axios from "axios";
import { ExamAttemptContext, StudentInfo, ExamAttemptedQuestionSet, AttemptedQuestionSetStatus, ExamAttemptScore, QuestionSetAttemptScore } from "../Common/types";
import { FiClock } from "react-icons/fi";
import QuestionSetRenderer, { RendererHandle, RendererStats } from "../Components/QuestionSetRenderer";
import { useNavigate } from "react-router-dom";
import eduviclogo from "../../resources/Eduvictechlogo.png";
import { FaClock, FaHourglassStart, FaFlag } from "react-icons/fa";
import ScoreViewer from "../Components/ScoreViewer";
import ExamSubmissionConfirmation from "../Components/ExamSubmissionConfirmation";
import { toUtcISOString, parseUtc, fmtLocal } from "../../utils/dates";
import { useTenant } from "../Routers/TenantContextProvider";

const STATUS_COLORS: Record<string, string> = {
  COMPLETED: "green",
  SKIPPED: "red",
  IN_PROGRESS: "purple",
  NOT_STARTED: "gray",
  LOCKED: "gray",
  EXPIRED: "orange",
};

const TOP_BAR_BG = { light: "gray.100", dark: "gray.700" };
const QUESTION_CARD_BG = { light: "blue.700", dark: "blue.700" };
const STATUS_BADGE_BG = { light: "blackAlpha.700", dark: "whiteAlpha.50" };
const TEXT_COLORS = { light: "gray.800", dark: "white" };

const LegendItem = ({
  color,
  label,
  flag = false,
  border = false
}: {
  color: string;
  label: string;
  flag?: boolean;
  border?: boolean;
}) => (
  <Flex align="center" gap={2}>
    <Box position="relative" w="20px" h="20px">
      <Box
        w="100%"
        h="100%"
        borderRadius="full"
        bg={color}
        border={border ? '2px solid' : 'none'}
        borderColor={border ? color : 'transparent'}
      />
      {flag && (
        <Box position="absolute" top="-6px" right="-6px" color="purple.500" fontSize="sm">
          <FaFlag />
        </Box>
      )}
    </Box>
    <Text fontSize="sm" whiteSpace="nowrap">{label}</Text>
  </Flex>
);

const StudentExamLobby = () => {
  const navigate = useNavigate();
  const { exam_attempt_id } = useParams<{ exam_attempt_id: string }>();
  const [currentStudent, setCurrentStudent] = useState<StudentInfo>();
  const [examContext, setExamContext] = useState<ExamAttemptContext | null>(null);
  const [examAttemptedQuestionSet, setExamAttemptedQuestionSet] = useState<ExamAttemptedQuestionSet | null>(null);
  const { tenant } = useTenant();

  const [examAttemptScore, setExamAttemptScore] = useState<ExamAttemptScore | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  const token = localStorage.getItem("token");
  const isAuthenticated = Boolean(token);

  const toast = useToast();
  const cardBg = useColorModeValue("gray.100", "gray.700");

  const apiUrl = process.env.REACT_APP_API_URL;

  const [activeQuestionInfo, setActiveQuestionInfo] = useState<{
      index: number;
      maxMarks: number;
      total: number;
    } | null>(null);

  const markExamInProgress = async (attemptId: string) =>
    axios.put(`${apiUrl}/exam-attempt/${attemptId}/mark-in-progress`, null, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });

  const markExamComplete = async (attemptId: string) =>
    axios.put(`${apiUrl}/exam-attempt/${attemptId}/mark-completed`, null, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });

  const autoSubmitQuestionSet = async (qsAttemptId: number) => {
    await qsRef.current?.submitAll();
    await axios.put(`${apiUrl}/exam-question-set-attempt/${qsAttemptId}/submit`, null, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    setExamAttemptedQuestionSet(null);
    setActiveQuestionInfo(null);  // ADD THIS
  };

  const submitQuestionSet = async (qsAttemptId: number) => {
    await qsRef.current?.submitAll();
    await axios.put(`${apiUrl}/exam-question-set-attempt/${qsAttemptId}/submit`, null, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    setActiveQuestionInfo(null);  // ADD THIS
  };

  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);

  // Time updater -- removed the fetchExamContext() call from the
  // "Mark Exam In-Progress" branch, same fix as B2C: avoid colliding
  // with the auto-start effect below. Status flip is enough; the
  // auto-start effect picks up the QS start once examContext updates.
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date(Date.now());
      setCurrentTime(now);
      const nowMs = now.getTime();

      if (!examContext || !exam_attempt_id) return;

      const examStart = parseUtc(examContext.expected_start_time).getTime();
      const examEnd = parseUtc(examContext.expected_end_time).getTime();

      if (examContext.status === "NOT_STARTED" && nowMs >= examStart) {
        markExamInProgress(exam_attempt_id).then(() => {
          setExamContext((prev) => prev ? { ...prev, status: "IN_PROGRESS" } : prev);
        });
      }

      if (examContext.status === "IN_PROGRESS" && nowMs >= examEnd) {
        setExamContext((prev) => prev ? { ...prev, status: "COMPLETED" } : prev);
        markExamComplete(exam_attempt_id).then(fetchExamContext);
        fetchExamScore();
      }

      const activeQSet = examContext.question_sets.find(
        (qs) => qs.question_set_id === examContext.active_question_set_id
      );

      if (activeQSet && (!cooldownUntil || nowMs >= cooldownUntil)) {
        const activeEnd = parseUtc(activeQSet.expected_end_time).getTime();

        if (nowMs >= activeEnd) {
          fetchExamContext();
          setCooldownUntil(nowMs + 30000);
        }
      }

      examContext.question_sets.forEach((qs) => {
        const qsEnd = parseUtc(qs.expected_end_time).getTime();

        if (qs.status === "IN_PROGRESS" && nowMs >= qsEnd && qs.exam_attempted_question_set_id) {
        //  setExamContext((prev) => prev ? { ...prev, status: "COMPLETED" } : prev);
          autoSubmitQuestionSet(qs.exam_attempted_question_set_id).then(fetchExamContext);
         // fetchExamScore();
        }
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [examContext, exam_attempt_id]);

  const fetchCurrentStudent = async () => {
    try {
      const response = await axios.get(`${apiUrl}/students-info`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setCurrentStudent(response.data);
    } catch (error) {
      console.error("Error fetching Student Info:", error);
    }
  };

  // fetchExamContext is now purely a context fetcher -- the /start API
  // call has been removed entirely, matching B2C. handleQuestionSetStart
  // is the single owner of starting a question set and setting
  // examAttemptedQuestionSet.
  const fetchExamContext = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}/exam-attempt/${exam_attempt_id}/context`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setExamContext(response.data);
    } catch (error) {
      toast({
        title: "Failed to fetch exam context",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchExamScore = async () => {
    if (!examContext) return;

    if (examContext.results_auto_publish) {
      try {
        setLoading(true);
        const response = await axios.get(
          `${apiUrl}/exam-attempt/${exam_attempt_id}/score`,
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        );
        setExamAttemptScore(response.data);
      } catch (error) {
        toast({
          title: "Failed to fetch exam score",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchCurrentStudent();
      fetchExamContext();
    }
  }, [exam_attempt_id]);

  useEffect(() => {
    if (examContext && examContext.status === "COMPLETED") {
      fetchExamScore();
    }
  }, [examContext]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (tenant) {
        navigate(`/platform/${tenant.tenant_slug}/login`);
      } else {
        navigate(`/platform/`);
      }
    }
  }, [isAuthenticated]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getTimerLabel = (start: Date, end: Date) => {
    const now = currentTime.getTime();
    const startTime = parseUtc(start).getTime();
    const endTime = parseUtc(end).getTime();

    if (now < startTime) {
      const diff = Math.floor((startTime - now) / 1000);
      return `Time to Start: ${formatDuration(diff)}`;
    } else if (now < endTime) {
      const diff = Math.floor((endTime - now) / 1000);
      return `Time Remaining: ${formatDuration(diff)}`;
    } else {
      return "Time Over";
    }
  };

    const getTimerLabelCompact = (start: Date, end: Date) => {

    const now = currentTime.getTime();
    const startTime = parseUtc(start).getTime();
    const endTime = parseUtc(end).getTime();
  
    if (now < startTime) {
      const diff = Math.floor((startTime - now) / 1000);
      return formatDuration(diff); // just the countdown, no label
    } else if (now < endTime) {
      const diff = Math.floor((endTime - now) / 1000);
      return formatDuration(diff);
    } else {
      return "0:00";
    }
  };

  const getTimerStatus = (start: Date, end: Date) => {
    const now = currentTime.getTime();
    const startTime = parseUtc(start).getTime();
    const endTime = parseUtc(end).getTime();
  
    if (now < startTime) return "Time to Start";
    if (now < endTime) return "Time Left";
    return "Time Over";
  };
  
  const getTimerValue = (start: Date, end: Date) => {
    const now = currentTime.getTime();
    const startTime = parseUtc(start).getTime();
    const endTime = parseUtc(end).getTime();
  
    if (now < startTime) {
      return formatDuration(Math.floor((startTime - now) / 1000));
    }
  
    if (now < endTime) {
      return formatDuration(Math.floor((endTime - now) / 1000));
    }
  
    return "0m 0s";
  };

  const CARD_HEIGHT = "45px";
  const CARD_WIDTH = "250px";

  const handleSubmitActiveQuestionSet = async () => {
    const activeQS = examContext?.question_sets.find(
      (qs) => qs.question_set_id === examContext.active_question_set_id
    );

    if (!activeQS || !activeQS.exam_attempted_question_set_id || !examContext?.exam_attempt_id) {
      console.error("Missing active question set or attempt ID.");
      return;
    }

    handleShowStats();
  };

  const handleConfirmSubmit = async () => {
    setIsConfirmationOpen(false);
    const activeQS = examContext?.question_sets.find(
      (qs) => qs.question_set_id === examContext.active_question_set_id
    );

    if (!activeQS || !activeQS.exam_attempted_question_set_id || !examContext?.exam_attempt_id) {
      console.error("Missing active question set or attempt ID.");
      return;
    }

    try {
      await submitQuestionSet(activeQS.exam_attempted_question_set_id);

      const qsList = examContext.question_sets;
      const currentIndex = qsList.findIndex(qs => qs.question_set_id === activeQS.question_set_id);
      const isLastSet = currentIndex === qsList.length - 1;

      if (isLastSet) {
        setExamContext((prev) => prev ? { ...prev, status: "COMPLETED" } : prev);
        await markExamComplete(examContext.exam_attempt_id.toString());
        fetchExamScore();
      } else {
        const nextQuestionSetId = qsList[currentIndex + 1].question_set_id;
        setExamContext({
          ...examContext,
          active_question_set_id: nextQuestionSetId,
        });
      }

      await fetchExamContext();
      setExamAttemptedQuestionSet(null);
    } catch (error) {
      console.error("Failed to submit question set:", error);
      alert("Failed to submit question set. Please try again.");
    }
  };

  // ── Auto-start: single owner of examAttemptedQuestionSet, guarded
  // against concurrent calls. Ported directly from B2C's Mock Exam path.
  const isStartingQSRef = useRef(false);

  const handleQuestionSetStart = async (questionSetId: number, examAttemptId: number) => {
    if (isStartingQSRef.current) return;
    isStartingQSRef.current = true;

    try {
      const response = await axios.post(
        `${apiUrl}/exam-question-set-attempt/${examAttemptId}/${questionSetId}/start`,
        null,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setExamAttemptedQuestionSet(response.data);
      await fetchExamContext();
    } catch (err) {
      console.error("Failed to start question set:", err);
      alert("Failed to start question set. Please try again.");
    } finally {
      isStartingQSRef.current = false;
    }
  };

  const { isOpen, onOpen, onClose } = useDisclosure();
  const qsRef = useRef<RendererHandle>(null);
  const [stats, setStats] = useState<RendererStats | null>(null);

  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const handleShowStats = () => {
    if (qsRef.current) {
      const latestStats = qsRef.current.getStats();
      setStats(latestStats);
      setIsConfirmationOpen(true);
    }
  };

  // ── Auto-start effect: detects NOT_STARTED + within time window,
  // starts automatically. No "Click to Start" button anymore.
  const autoStartedRef = useRef<number | null>(null);

  useEffect(() => {
    if (!examContext) return;

    const activeQS = examContext.question_sets.find(
      (qs) => qs.question_set_id === examContext.active_question_set_id
    );

    if (!activeQS) return;

    const now = currentTime.getTime();
    const qsStart = parseUtc(activeQS.expected_start_time).getTime();
    const qsEnd = parseUtc(activeQS.expected_end_time).getTime();

    const isReadyToStart =
      activeQS.status === "NOT_STARTED" && now >= qsStart && now <= qsEnd;

    if (isReadyToStart && autoStartedRef.current !== activeQS.question_set_id) {
      autoStartedRef.current = activeQS.question_set_id;
      handleQuestionSetStart(activeQS.question_set_id, examContext.exam_attempt_id);
    }
  }, [examContext, currentTime]);

  // ── Recovery effect: handles page reload mid-attempt, where
  // examContext shows IN_PROGRESS but local renderer state is empty.
  useEffect(() => {
    if (!examContext || examAttemptedQuestionSet) return;

    const activeQS = examContext.question_sets.find(
      (qs) => qs.question_set_id === examContext.active_question_set_id
    );

    if (activeQS?.status === "IN_PROGRESS" && examContext.exam_attempt_id) {
      handleQuestionSetStart(activeQS.question_set_id, examContext.exam_attempt_id);
    }
  }, [examContext?.active_question_set_id]);

  const topBarBg = useColorModeValue(TOP_BAR_BG.light, TOP_BAR_BG.dark);
  const questionCardBg = useColorModeValue(QUESTION_CARD_BG.light, QUESTION_CARD_BG.dark);
  const statusBadgeBg = useColorModeValue(STATUS_BADGE_BG.light, STATUS_BADGE_BG.dark);
  const textColor = useColorModeValue(TEXT_COLORS.light, TEXT_COLORS.dark);

  return (
    <Box as="main" mx="auto" p={0}>

    {loading ? (
      <Flex align="center" justify="center" mt={6}>
        <Spinner size="lg" />
      </Flex>
    ) : examContext ? (
      <Flex
        align="center"
        justify="space-between"
        position="sticky"
        top={0}
        zIndex={10}
        background={topBarBg}
        px={4}
        py={1}
        h={CARD_HEIGHT}
      >
        <Flex align="center" overflowX="auto" wrap="nowrap" flex={1} minW={0}>
          <Image
            src={eduviclogo}
            alt="Logo"
            boxSize={CARD_HEIGHT}
            objectFit="contain"
            border="md"
            bg="blue.700"
            mr={{ base: 2, md: 8 }}
            flexShrink={0}
          />

          <Flex
            direction="column"
            justify="center"
            bg={topBarBg}
            px={3}
            py={1}
            minW={0}
            maxW={CARD_WIDTH}
            h={CARD_HEIGHT}
            mr={2}
            color={textColor}
            borderRadius="none"
            flexShrink={1}
          >
            <Flex align="center" justify="space-between" gap={2}>
              <Text
                fontSize="sm"
                fontWeight="bold"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                maxW={{ base: "120px", md: "150px" }}
              >
                {examContext.exam_title}
              </Text>

              {/* Status badge hidden on mobile to save space — timer/title take priority */}
              <Badge
                fontSize="0.7em"
                variant="solid"
                colorScheme={STATUS_COLORS[examContext.status]}
                px={1}
              >
                {examContext.status.replace(/_/g, " ")}
              </Badge>
            </Flex>

            {(examContext.status === "IN_PROGRESS" || examContext.status === "NOT_STARTED") && (
              <Flex align="center" mt={1} gap={1}>
                <Icon as={FiClock} w={3} h={3} flexShrink={0} />

                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          whiteSpace="nowrap"
                        >
                          Exam {getTimerStatus(
                            parseUtc(examContext.expected_start_time),
                            parseUtc(examContext.expected_end_time)
                          )}
                        </Text>

                        <Badge
                          bg="gray.900"
                          border="1px solid"
                          borderColor="green.600"
                          color="green.300"
                          fontFamily="mono"
                          fontWeight="bold"
                          px={2}
                          py={1}
                          borderRadius="md"
                        >
                          {getTimerValue(
                            parseUtc(examContext.expected_start_time),
                            parseUtc(examContext.expected_end_time)
                          )}
                        </Badge>
              </Flex>
            )}
          </Flex>

          {/* Question set connector/card row — desktop only.
              On mobile this detail is already shown in the Question Set Bar below. */}
          <Flex display={{ base: "none", md: "flex" }} align="center">
            {examContext.question_sets.length > 0 && (
              <Box
                w="30px"
                h="4px"
                bgColor={examContext.question_sets[0].status === "COMPLETED" ? "green.400" : "gray.300"}
                mx={2}
                borderRadius="md"
              />
            )}

            {examContext.question_sets
              .sort((a, b) => a.sequence - b.sequence)
              .map((qs, index) => {
                const isActive = qs.question_set_id === examContext.active_question_set_id;

                return (
                  <React.Fragment key={qs.question_set_id}>
                    <Flex
                      direction="column"
                      justify="center"
                      bg={isActive ? questionCardBg : "blue.600"}
                      px={3}
                      py={1}
                      minW={CARD_WIDTH}
                      maxW={CARD_WIDTH}
                      h={CARD_HEIGHT}
                      borderRadius="sm"
                      textAlign="left"
                      overflow="hidden"
                      opacity={isActive ? 1 : 0.4}
                    >
                      <Flex align="center" justify="space-between">
                        <Text
                          fontSize="sm"
                          fontWeight="bold"
                          whiteSpace="nowrap"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          maxW="110px"
                          color="white"
                        >
                          {qs.title}
                        </Text>
                        <Badge colorScheme={STATUS_COLORS[qs.status]} fontSize="0.7em" variant="subtle" px={1}>
                          {qs.status.replace(/_/g, " ")}
                        </Badge>
                      </Flex>
                    </Flex>

                    {index < examContext.question_sets.length - 1 && (
                      <Box
                        w="30px"
                        h="4px"
                        bgColor={examContext.question_sets[index].status === "COMPLETED" ? "green.400" : "gray.300"}
                        mx={2}
                        borderRadius="md"
                      />
                    )}
                  </React.Fragment>
                );
              })}
          </Flex>
        </Flex>

        <Flex
          direction="column"
          justify="center"
          bg={topBarBg}
          px={3}
          py={1}
          borderRadius="none"
          minW={{ base: "60px", md: "150px" }}
          maxW={{ base: "60px", md: "150px" }}
          h={CARD_HEIGHT}
          color={textColor}
          textAlign="left"
          flexShrink={0}
        >
          <Text
            fontWeight="semibold"
            fontSize={{ base: "xs", md: "md" }}
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
          >
            {currentStudent?.givenName}
          </Text>
        </Flex>
      </Flex>
    ) : null}

    {examContext && (() => {
      const activeQS = examContext.question_sets.find(
        (qs) => qs.question_set_id === examContext.active_question_set_id
      );

      if (!activeQS) return null;

      return (
        <>
          <Box
            borderRadius="sm"
            p={2}
            bg="blue.700"
            boxShadow="md"
            border="1px solid"
            borderColor="gray.300"
            position="sticky"
            top="45px"
            zIndex="sticky"
          >
            <Flex
              align="center"
              justify="space-between"
              minH="40px"
              px={{ base: 2, md: 6 }}
              gap={2}
              wrap="nowrap"
            >

              {/* LEFT — timer: icon + time, verbose hidden on mobile */}
<Flex align="center" gap={2} flexShrink={0}>
  <Icon as={FiClock} w={4} h={4} color="gray.300" flexShrink={0} />

  {/* Mobile */}
  <Badge
    display={{ base: "inline-flex", md: "none" }}
    bg="gray.900"
    border="1px solid"
    borderColor="green.600"
    color="green.300"
    fontFamily="mono"
    fontWeight="bold"
    px={2}
    py={1}
    borderRadius="md"
    fontSize="xs"
  >
    {getTimerValue(
      parseUtc(activeQS.expected_start_time),
      parseUtc(activeQS.expected_end_time)
    )}
  </Badge>

  {/* Desktop */}
  <Flex
    display={{ base: "none", md: "inline-flex" }}
    align="center"
    gap={2}
  >
    <Text
      fontSize="sm"
      color="gray.300"
      fontWeight="semibold"
      whiteSpace="nowrap"
    >
      {getTimerStatus(
        parseUtc(activeQS.expected_start_time),
        parseUtc(activeQS.expected_end_time)
      )}
    </Text>

    <Badge
      bg="gray.900"
      border="1px solid"
      borderColor="green.600"
      color="green.300"
      fontFamily="mono"
      fontWeight="bold"
      px={3}
      py={1}
      borderRadius="md"
      minW="90px"
      textAlign="center"
    >
      {getTimerValue(
        parseUtc(activeQS.expected_start_time),
        parseUtc(activeQS.expected_end_time)
      )}
    </Badge>
  </Flex>
</Flex>

              {/* CENTRE — question info, shrinks gracefully on mobile */}
              <Flex align="center" justify="center" flex={1} minW={0}>
                {activeQuestionInfo ? (
                  <Text
                    fontWeight="bold"
                    color="white"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    fontSize={{ base: "md", md: "lg" }}
                  >
                    {/* Mobile: compact Q x/y */}
                    <Text as="span" display={{ base: "inline", md: "none" }}>
                      Q {activeQuestionInfo.index + 1}/{activeQuestionInfo.total}
                    </Text>
                    {/* Desktop: full label with max marks */}
                    <Text as="span" display={{ base: "none", md: "inline" }}>
                      Question {activeQuestionInfo.index + 1} of {activeQuestionInfo.total}
                      &nbsp;·&nbsp;
                      Max Marks: {activeQuestionInfo.maxMarks}
                    </Text>
                  </Text>
                ) : (
                  <Text
                    fontWeight="bold"
                    color="white"
                    fontSize={{ base: "xs", md: "lg" }}
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    {activeQS.title}
                  </Text>
                )}
              </Flex>

              {/* RIGHT — submit always visible, sized for mobile */}
              <Box flexShrink={0}>
                {activeQS.status === "IN_PROGRESS" ? (
                  <Button
                    colorScheme="gray"
                    variant="solid"
                    size={{ base: "sm", md: "lg" }}
                    shadow="2xl"
                    onClick={handleSubmitActiveQuestionSet}
                  >
                    Submit
                  </Button>
                ) : (
                  /* Invisible placeholder preserves layout balance on desktop only */
                  <Box display={{ base: "none", md: "block" }} w="80px" />
                )}
              </Box>

            </Flex>
          </Box>

          {activeQS.status === "IN_PROGRESS" && examAttemptedQuestionSet && (
            <Box borderRadius="sm" boxShadow="md" border="1px solid" borderColor="gray.200">
              <QuestionSetRenderer
                examAttemptedQuestionSetProp={examAttemptedQuestionSet}
                ref={qsRef}
                onQuestionChange={(index, question) => {
                  setActiveQuestionInfo({
                    index,
                    maxMarks: question.max_marks ?? 0,
                    total: examAttemptedQuestionSet.attempted_questions.length,
                  });
                }}
              />
            </Box>
          )}


                <Modal isOpen={isConfirmationOpen} onClose={() => setIsConfirmationOpen(false)} isCentered size="xl">
                  <ModalOverlay />
                  <ModalContent borderRadius="lg" px={4}>
                    <ModalHeader fontWeight="bold" textAlign="center">Review & Confirm Submission</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                      {stats && (
                        <Box borderWidth="1px" borderRadius="md" p={4} mb={4}>
                          <Text fontWeight="semibold" mb={3}>
                            Question Summary
                          </Text>

                          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                            <LegendItem color="green.400" label={`Answered (No Flag): ${stats.answeredWithoutFlag}`} />
                            <LegendItem color="green.400" label={`Answered + Flagged: ${stats.answeredWithFlag}`} flag />
                            <LegendItem color="red.400" label={`Visited (No Flag): ${stats.visitedWithoutFlag}`} />
                            <LegendItem color="red.400" label={`Visited + Flagged: ${stats.visitedWithFlag}`} flag />
                            <LegendItem color="gray.400" label={`Not Visited: ${stats.notVisited}`} />
                          </Grid>

                          <Text mt={4} fontWeight="medium">
                            <strong>Total Questions:</strong> {stats.total}
                          </Text>
                        </Box>
                      )}

                      <Text color="red.500" fontWeight="medium" mt={2} textAlign="center">
                        Are you sure you want to submit this question set? This action cannot be undone.
                      </Text>
                    </ModalBody>

                    <ModalFooter>
                      <Button colorScheme="blue" mr={3} onClick={handleConfirmSubmit}>
                        Confirm Submit
                      </Button>
                      <Button variant="ghost" onClick={() => setIsConfirmationOpen(false)}>
                        Cancel
                      </Button>
                    </ModalFooter>
                  </ModalContent>
                </Modal>
              </>
            );
          })()}

      {examContext && (examContext.status === "COMPLETED") && (
        <Box>
          {examAttemptScore ? (
            <ScoreViewer scoreData={examAttemptScore} />
          ) : (
            currentStudent &&
            examContext &&
            <ExamSubmissionConfirmation
              student_email={currentStudent.email}
              student_given_name={currentStudent.givenName}
              exam_title={examContext.exam_title}
              student_end_time={fmtLocal(examContext.student_end_time, {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              }).replace(",", " at")}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default StudentExamLobby;